import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const TIERS = [
  { n: '01', name: 'cub',                ci: '0 ci',          w: 6 },
  { n: '02', name: 'stringer',           ci: '8 ci',          w: 20 },
  { n: '03', name: 'correspondent',      ci: '20 ci',         w: 38 },
  { n: '04', name: 'staff writer',       ci: '30 ci',         w: 56 },
  { n: '05', name: 'beat reporter',      ci: '60 ci',         w: 76 },
  { n: '06', name: 'feature writer',     ci: '90 ci',         w: 98 },
  { n: '07', name: 'columnist',          ci: '140 ci',        w: 120 },
  { n: '08', name: 'senior correspondent', ci: '200 ci',      w: 144 },
  { n: '09', name: 'contributing editor', ci: '300 ci',       w: 170 },
  { n: '10', name: 'senior editor',      ci: '420 ci',        w: 198 },
  { n: '11', name: 'editor at large',    ci: '580 ci',        w: 228 },
  { n: '12', name: 'the voice ◆',        ci: 'by appointment', w: 260, voice: true },
]

const SOCIALS = [
  { name: 'instagram',  handle: '@themargins_journal',               url: 'https://www.instagram.com/themargins_journal/',               desc: 'excerpts and behind the scenes.' },
  { name: 'discord',    handle: 'the margins server',                url: 'https://discord.gg/MRbakEXjPM',                               desc: 'the conversation continues here.' },
  { name: 'linkedin',   handle: 'the margins journal',               url: 'https://www.linkedin.com/company/the-margins-journal/',        desc: 'updates and announcements.' },
]

export default function CommunityPage() {
  const { user } = useAuth()
  const [hoveredTier, setHoveredTier] = useState(null)

  return (
    <div style={{ paddingTop: '4.5rem', background: 'var(--paper)', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* ── HERO ── */}
      <section style={s.hero}>
        <div style={s.marginLine} />
        <div style={s.heroInner}>
          <p style={s.eyebrow}>the community</p>
          <h1 style={s.title}>
            who's here.<br />
            how it <span style={{ color: 'var(--accent)' }}>works.</span>
          </h1>
          <p style={s.heroSub}>
            the margins is built around a simple idea: the more you show up and mean it,
            the more you earn. not followers. not likes. credibility — in a room that actually reads.
          </p>
        </div>
      </section>

      <div style={s.cut} />

      {/* ── THE PRESS / RANK SYSTEM ── */}
      <section style={s.pressSection}>
        <div style={s.pressInner}>
          <p style={s.sectionTag}>the press — rank system</p>
          <h2 style={s.pressHed}>twelve ranks.<br />each one means something real.</h2>
          <p style={s.pressSub}>
            every piece you publish earns column inches (CI). so do reactions and comments on your work.
            your rank is a live record of how much you've shown up. the voice is by appointment only —
            you cannot grind your way there.
          </p>

          {/* CI explanation callout */}
          <div style={s.ciCallout}>
            <p style={s.ciCalloutLabel}>column inches — your currency</p>
            <div style={s.ciGrid}>
              {[
                { action: 'short piece (under 600 words)', ci: '+8 CI' },
                { action: 'mid piece (600–1200 words)',    ci: '+15 CI' },
                { action: 'long piece (1200+ words)',      ci: '+22 CI' },
                { action: '"brought here" reaction',      ci: '+5 CI' },
                { action: '"shifts something" reaction',  ci: '+4 CI' },
                { action: '"sits with me" reaction',      ci: '+3 CI' },
                { action: 'other reaction',               ci: '+2 CI' },
                { action: 'comment received',             ci: '+3 CI' },
              ].map((row, i) => (
                <div key={i} style={s.ciRow}>
                  <span style={s.ciAction}>{row.action}</span>
                  <span style={s.ciNum}>{row.ci}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tier list */}
          <div style={s.tiersBox}>
            {TIERS.map((t, i) => (
              <div
                key={i}
                style={{
                  ...s.tier,
                  ...(t.voice ? s.tierVoice : {}),
                  ...(hoveredTier === i ? { background: 'rgba(200,64,26,.06)', paddingLeft: '1rem' } : {}),
                  transition: 'background .15s, padding-left .15s',
                }}
                onMouseEnter={() => setHoveredTier(i)}
                onMouseLeave={() => setHoveredTier(null)}
              >
                <span style={{ ...s.tnum, ...(hoveredTier === i ? { color: 'var(--accent)' } : {}) }}>{t.n}</span>
                <div style={{ height: 2, background: 'var(--accent)', width: hoveredTier === i ? Math.min(t.w * 1.15, 260) : t.w, opacity: hoveredTier === i ? .8 : .45, flexShrink: 0, transition: 'width .25s ease, opacity .2s' }} />
                <span style={{
                  ...s.tname,
                  ...(t.voice ? { color: 'var(--accent)' } : {}),
                  ...(hoveredTier === i && !t.voice ? { color: 'var(--ink)' } : {}),
                }}>{t.name}</span>
                <span style={{ ...s.tci, ...(t.voice ? { color: 'var(--muted)' } : {}), ...(hoveredTier === i ? { color: 'var(--accent)' } : {}) }}>{t.ci}</span>
              </div>
            ))}
          </div>

          <Link to="/dashboard" style={s.pressLink}>see your rank →</Link>
        </div>
      </section>

      <div style={s.cut} />

      {/* ── FOUNDING MEMBERS ── */}
      <section style={s.foundingSection}>
        <div style={s.foundingInner}>
          <p style={s.sectionTag}>founding members</p>
          <h2 style={s.foundingHed}>first 100.<br />permanent badge.<br />can't be bought later.</h2>
          <p style={s.pressSub}>
            when the margins becomes something — and it will — the founding badge on your profile
            says you were here before it did. that's not a feature. that's history.
          </p>
          <div style={s.foundingGrid}>
            {[
              { num: '100', label: 'founding spots total',    desc: 'a small group. intentional.' },
              { num: '∞',   label: 'badge lasts forever',     desc: 'on your profile. always. no matter your rank.' },
              { num: 'free.', label: 'no cost, ever',         desc: 'sign up and write. that\'s it.', small: true },
            ].map((f, i) => (
              <div key={i} style={s.fcell}>
                <div style={{ ...s.fcellNum, ...(f.small ? { fontSize: '1.6rem' } : {}) }}>{f.num}</div>
                <div style={s.fcellLabel}>{f.label}</div>
                <p style={s.fcellDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={s.cut} />

      {/* ── SOCIAL PLATFORMS ── */}
      <section style={s.socialsSection}>
        <div style={s.socialsInner}>
          <p style={s.sectionTag}>find us elsewhere</p>
          <h2 style={{ ...s.pressHed, marginBottom: '0.6rem' }}>the conversation<br />doesn't stop here.</h2>
          <p style={{ ...s.pressSub, marginBottom: '2.5rem' }}>
            follow along, join the server, stay in the loop.
          </p>
          <div style={s.socialsList}>
            {SOCIALS.map((soc, i) => (
              <a key={i} href={soc.url} target="_blank" rel="noreferrer" style={s.socialCard} className="post-hover">
                <div style={s.socialName}>{soc.name}</div>
                <div style={s.socialHandle}>{soc.handle}</div>
                <div style={s.socialDesc}>{soc.desc}</div>
                <span style={s.socialArrow}>→</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <div style={s.cut} />

      {/* ── CTA ── */}
      <section style={{ padding: '5rem 2rem', background: 'var(--cream)' }}>
        <div style={{ paddingLeft: '2.8rem' }}>
          <p style={s.sectionTag}>ready?</p>
          <h2 style={{ ...s.pressHed, marginBottom: '1rem' }}>come say something.</h2>
          <p style={{ ...s.pressSub, marginBottom: '2rem' }}>
            sign up. start writing. that's it.
          </p>
          <Link to={user ? '/write' : '/auth'} style={s.ctaBtn}>
            {user ? 'start writing →' : 'join the margins →'}
          </Link>
        </div>
      </section>

    </div>
  )
}

const s = {
  hero: {
    padding: '5rem 2rem 4rem',
    position: 'relative', overflow: 'hidden',
  },
  marginLine: {
    position: 'absolute', left: '2rem', top: 0, bottom: 0,
    width: 2, background: 'var(--accent)', opacity: .4,
  },
  heroInner: { paddingLeft: '2.8rem', maxWidth: 640, position: 'relative', zIndex: 1 },
  eyebrow: {
    fontFamily: "'DM Mono', monospace", fontSize: '.54rem',
    letterSpacing: '.2em', color: 'var(--accent)', marginBottom: '1.2rem',
  },
  title: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(2.2rem, 6vw, 4rem)', lineHeight: .95,
    color: 'var(--ink)', letterSpacing: '-.03em', marginBottom: '1.2rem',
  },
  heroSub: {
    fontSize: '1rem', color: 'var(--muted)', lineHeight: 1.75, maxWidth: '42ch',
  },
  cut: { height: 1, background: 'var(--rule)' },
  sectionTag: {
    fontFamily: "'DM Mono', monospace", fontSize: '.54rem',
    letterSpacing: '.2em', color: 'var(--accent)', marginBottom: '1.4rem',
  },
  pressSection: { padding: '5rem 2rem', background: 'var(--paper)' },
  pressInner: { paddingLeft: '2.8rem' },
  pressHed: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(1.8rem, 5vw, 3rem)', lineHeight: .95,
    color: 'var(--ink)', letterSpacing: '-.02em', marginBottom: '.8rem',
  },
  pressSub: {
    fontSize: '.95rem', color: 'var(--muted)', lineHeight: 1.75,
    maxWidth: '48ch', marginBottom: '2.5rem',
  },
  ciCallout: {
    border: '1px solid var(--rule)', padding: '1.5rem',
    background: 'var(--cream)', marginBottom: '2rem', maxWidth: 560,
  },
  ciCalloutLabel: {
    fontFamily: "'DM Mono', monospace", fontSize: '.52rem',
    letterSpacing: '.14em', color: 'var(--muted)', marginBottom: '1rem',
  },
  ciGrid: { display: 'flex', flexDirection: 'column', gap: '.4rem' },
  ciRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '.35rem 0', borderBottom: '1px solid var(--rule)',
  },
  ciAction: {
    fontFamily: "'DM Mono', monospace", fontSize: '.54rem',
    letterSpacing: '.06em', color: 'var(--muted)',
  },
  ciNum: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '.72rem', color: 'var(--accent)',
  },
  tiersBox: {
    display: 'flex', flexDirection: 'column',
    border: '1px solid var(--rule)', marginBottom: '2rem', maxWidth: 600,
  },
  tier: {
    display: 'flex', alignItems: 'center', gap: '.75rem',
    padding: '.55rem .75rem', borderBottom: '1px solid var(--rule)',
  },
  tierVoice: {},
  tnum: {
    fontFamily: "'DM Mono', monospace", fontSize: '.46rem',
    color: 'var(--muted)', minWidth: 16,
  },
  tname: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '.72rem',
    fontWeight: 500, color: 'var(--muted)', minWidth: 160,
    letterSpacing: '-.01em',
  },
  tci: {
    fontFamily: "'DM Mono', monospace", fontSize: '.46rem',
    color: 'var(--muted)', marginLeft: 'auto',
  },
  pressLink: {
    fontFamily: "'DM Mono', monospace", fontSize: '.6rem',
    letterSpacing: '.1em', color: 'var(--accent)', textDecoration: 'none',
  },
  foundingSection: { padding: '5rem 2rem', background: 'var(--cream)' },
  foundingInner: { paddingLeft: '2.8rem' },
  foundingHed: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: 'clamp(2rem, 5vw, 3.2rem)', lineHeight: .95,
    color: 'var(--ink)', letterSpacing: '-.02em', marginBottom: '.8rem',
  },
  foundingGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 1, background: 'var(--rule)', border: '1px solid var(--rule)',
    maxWidth: 640,
  },
  fcell: {
    background: 'var(--paper)', padding: '1.5rem',
  },
  fcellNum: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '2.2rem', color: 'var(--accent)',
    letterSpacing: '-.03em', lineHeight: 1, marginBottom: '.35rem',
  },
  fcellLabel: {
    fontFamily: "'DM Mono', monospace", fontSize: '.5rem',
    letterSpacing: '.14em', color: 'var(--muted)', marginBottom: '.4rem',
  },
  fcellDesc: { fontSize: '.8rem', color: 'var(--muted)', lineHeight: 1.5 },
  socialsSection: { padding: '5rem 2rem', background: 'var(--paper)' },
  socialsInner: { paddingLeft: '2.8rem' },
  socialsList: {
    display: 'flex', flexDirection: 'column',
    border: '1px solid var(--rule)', maxWidth: 600,
  },
  socialCard: {
    display: 'flex', alignItems: 'center', gap: '1.5rem',
    padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--rule)',
    textDecoration: 'none', transition: 'background .15s', cursor: 'pointer', position: 'relative',
  },
  socialName: {
    fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700,
    fontSize: '.95rem', color: 'var(--ink)', minWidth: 90,
  },
  socialHandle: {
    fontFamily: "'DM Mono', monospace", fontSize: '.54rem',
    color: 'var(--muted)', letterSpacing: '.06em', flex: 1,
  },
  socialDesc: { fontSize: '.82rem', color: 'var(--muted)' },
  socialArrow: {
    fontFamily: "'DM Mono', monospace", fontSize: '1rem',
    color: 'var(--muted)', marginLeft: 'auto',
  },
  ctaBtn: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '.95rem',
    fontWeight: 700, letterSpacing: '-.01em',
    color: 'var(--paper)', background: 'var(--ink)',
    padding: '.7rem 1.8rem', border: 'none', cursor: 'pointer',
    transition: 'all .25s', display: 'inline-block', textDecoration: 'none',
  },
}
